/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/

/**
 * Static-generated provenance for every ComponentColors token that is
 * built directly from a v1 primitive or semantic (as opposed to a literal
 * like 'transparent' or a ramp-unit string). Generated by reading
 * `primitiveDarkColors.X` / `primitiveLightColors.X` / `semanticColors.X`
 * references straight out of `color-component-tokens.ts` for each theme —
 * this is the only reliable way to know whether a component color's
 * *resolved* value originated from a primitive or a semantic, since two
 * different source tokens frequently resolve to the identical value (e.g.
 * `#000000` is simultaneously `black`, `brandAi0`, and (in the light
 * theme) `contentPrimary`).
 *
 * A small number of keys (e.g. `ratingStroke`) are sourced differently
 * between the two themes, which is exactly why this is two separate tables
 * rather than one merged one.
 *
 * Regenerate by re-running the generation against
 * `themes/{dark,light}-theme/color-component-tokens.ts` if either file's
 * source tokens change.
 */
export type ComponentColorProvenance = {
  kind: 'primitive' | 'semantic';
  sourceKey: string;
};
export const DARK_COMPONENT_COLOR_PROVENANCE: Record<string, ComponentColorProvenance> = {
  bannerActionHighInfo: {
    kind: 'primitive',
    sourceKey: 'blue300Dark'
  },
  bannerActionHighNegative: {
    kind: 'primitive',
    sourceKey: 'red300Dark'
  },
  bannerActionHighPositive: {
    kind: 'primitive',
    sourceKey: 'green300Dark'
  },
  bannerActionHighWarning: {
    kind: 'primitive',
    sourceKey: 'yellow300Dark'
  },
  bannerActionLowInfo: {
    kind: 'primitive',
    sourceKey: 'blue200Dark'
  },
  bannerActionLowNegative: {
    kind: 'primitive',
    sourceKey: 'red200Dark'
  },
  bannerActionLowPositive: {
    kind: 'primitive',
    sourceKey: 'green200Dark'
  },
  bannerActionLowWarning: {
    kind: 'primitive',
    sourceKey: 'yellow200Dark'
  },
  bottomNavigationSelectedText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  bottomNavigationText: {
    kind: 'primitive',
    sourceKey: 'gray600Dark'
  },
  breadcrumbsSeparatorFill: {
    kind: 'semantic',
    sourceKey: 'contentTertiary'
  },
  breadcrumbsText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  buttonDangerPrimaryActiveOverlay: {
    kind: 'semantic',
    sourceKey: 'pressedOverlayAlpha'
  },
  buttonDangerPrimaryFill: {
    kind: 'semantic',
    sourceKey: 'backgroundNegative'
  },
  buttonDangerPrimaryHoverOverlay: {
    kind: 'semantic',
    sourceKey: 'hoverOverlayAlpha'
  },
  buttonDangerPrimaryLoadingFill: {
    kind: 'semantic',
    sourceKey: 'backgroundNegative'
  },
  buttonDangerPrimarySelectedFill: {
    kind: 'semantic',
    sourceKey: 'backgroundNegative'
  },
  buttonDangerPrimarySelectedText: {
    kind: 'semantic',
    sourceKey: 'contentOnColor'
  },
  buttonDangerPrimarySpinnerBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  buttonDangerPrimarySpinnerForeground: {
    kind: 'semantic',
    sourceKey: 'backgroundAccent'
  },
  buttonDangerPrimaryText: {
    kind: 'semantic',
    sourceKey: 'contentOnColor'
  },
  buttonDangerSecondaryActiveOverlay: {
    kind: 'semantic',
    sourceKey: 'pressedOverlayAlpha'
  },
  buttonDangerSecondaryFill: {
    kind: 'semantic',
    sourceKey: 'backgroundSecondary'
  },
  buttonDangerSecondaryHoverOverlay: {
    kind: 'semantic',
    sourceKey: 'hoverOverlayAlpha'
  },
  buttonDangerSecondaryLoadingFill: {
    kind: 'semantic',
    sourceKey: 'backgroundSecondary'
  },
  buttonDangerSecondarySelectedFill: {
    kind: 'semantic',
    sourceKey: 'backgroundNegative'
  },
  buttonDangerSecondarySelectedText: {
    kind: 'semantic',
    sourceKey: 'contentOnColor'
  },
  buttonDangerSecondarySpinnerBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  buttonDangerSecondarySpinnerForeground: {
    kind: 'semantic',
    sourceKey: 'backgroundAccent'
  },
  buttonDangerSecondaryText: {
    kind: 'semantic',
    sourceKey: 'contentNegative'
  },
  buttonDangerTertiaryActiveOverlay: {
    kind: 'semantic',
    sourceKey: 'pressedOverlayAlpha'
  },
  buttonDangerTertiaryHoverOverlay: {
    kind: 'semantic',
    sourceKey: 'hoverOverlayAlpha'
  },
  buttonDangerTertiaryOuterBorder: {
    kind: 'semantic',
    sourceKey: 'tagRedBorderSecondarySelected'
  },
  buttonDangerTertiarySelectedFill: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  buttonDangerTertiarySelectedText: {
    kind: 'semantic',
    sourceKey: 'contentNegative'
  },
  buttonDangerTertiarySpinnerBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  buttonDangerTertiarySpinnerForeground: {
    kind: 'semantic',
    sourceKey: 'backgroundAccent'
  },
  buttonDangerTertiaryText: {
    kind: 'semantic',
    sourceKey: 'contentNegative'
  },
  buttonDisabledActiveFill: {
    kind: 'semantic',
    sourceKey: 'backgroundStateDisabled'
  },
  buttonDisabledActiveText: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  buttonDisabledFill: {
    kind: 'semantic',
    sourceKey: 'backgroundStateDisabled'
  },
  buttonDisabledSpinnerBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  buttonDisabledSpinnerForeground: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  buttonDisabledText: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  buttonFocusOuterBorder: {
    kind: 'semantic',
    sourceKey: 'borderAccent'
  },
  buttonInnerBorder: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  },
  buttonOuterBorder: {
    kind: 'semantic',
    sourceKey: 'borderSelected'
  },
  buttonOutlineActiveOverlay: {
    kind: 'semantic',
    sourceKey: 'pressedOverlayAlpha'
  },
  buttonOutlineFocusFill: {
    kind: 'semantic',
    sourceKey: 'backgroundTertiary'
  },
  buttonOutlineHoverOverlay: {
    kind: 'semantic',
    sourceKey: 'hoverOverlayAlpha'
  },
  buttonOutlineOuterBorder: {
    kind: 'semantic',
    sourceKey: 'borderOpaque'
  },
  buttonOutlineSelectedText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  buttonOutlineSpinnerBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  buttonOutlineSpinnerForeground: {
    kind: 'semantic',
    sourceKey: 'backgroundAccent'
  },
  buttonOutlineText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  buttonPrimaryActive: {
    kind: 'primitive',
    sourceKey: 'gray600Dark'
  },
  buttonPrimaryActiveOverlay: {
    kind: 'semantic',
    sourceKey: 'pressedOverlayInverseAlpha'
  },
  buttonPrimaryFill: {
    kind: 'semantic',
    sourceKey: 'backgroundInversePrimary'
  },
  buttonPrimaryHover: {
    kind: 'primitive',
    sourceKey: 'gray700Dark'
  },
  buttonPrimaryHoverOverlay: {
    kind: 'semantic',
    sourceKey: 'hoverOverlayInverseAlpha'
  },
  buttonPrimaryLoadingFill: {
    kind: 'semantic',
    sourceKey: 'backgroundInversePrimary'
  },
  buttonPrimarySelectedFill: {
    kind: 'semantic',
    sourceKey: 'backgroundInversePrimary'
  },
  buttonPrimarySelectedText: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  },
  buttonPrimarySpinnerBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  buttonPrimarySpinnerForeground: {
    kind: 'semantic',
    sourceKey: 'backgroundAccent'
  },
  buttonPrimaryText: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  },
  buttonSecondaryActive: {
    kind: 'primitive',
    sourceKey: 'gray400Dark'
  },
  buttonSecondaryActiveOverlay: {
    kind: 'semantic',
    sourceKey: 'pressedOverlayAlpha'
  },
  buttonSecondaryFill: {
    kind: 'semantic',
    sourceKey: 'backgroundSecondary'
  },
  buttonSecondaryHover: {
    kind: 'primitive',
    sourceKey: 'gray300Dark'
  },
  buttonSecondaryHoverOverlay: {
    kind: 'semantic',
    sourceKey: 'hoverOverlayAlpha'
  },
  buttonSecondaryLoadingFill: {
    kind: 'semantic',
    sourceKey: 'backgroundSecondary'
  },
  buttonSecondarySelectedFill: {
    kind: 'semantic',
    sourceKey: 'backgroundInversePrimary'
  },
  buttonSecondarySelectedText: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  },
  buttonSecondarySpinnerBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  buttonSecondarySpinnerForeground: {
    kind: 'semantic',
    sourceKey: 'backgroundAccent'
  },
  buttonSecondaryText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  buttonTertiaryActive: {
    kind: 'primitive',
    sourceKey: 'gray200Dark'
  },
  buttonTertiaryActiveOverlay: {
    kind: 'semantic',
    sourceKey: 'pressedOverlayAlpha'
  },
  buttonTertiaryDisabledActiveFill: {
    kind: 'semantic',
    sourceKey: 'backgroundStateDisabled'
  },
  buttonTertiaryDisabledActiveText: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  buttonTertiaryFocusFill: {
    kind: 'semantic',
    sourceKey: 'backgroundTertiary'
  },
  buttonTertiaryHover: {
    kind: 'primitive',
    sourceKey: 'gray100Dark'
  },
  buttonTertiaryHoverOverlay: {
    kind: 'semantic',
    sourceKey: 'hoverOverlayAlpha'
  },
  buttonTertiarySelectedText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  buttonTertiarySpinnerBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundTertiary'
  },
  buttonTertiarySpinnerForeground: {
    kind: 'semantic',
    sourceKey: 'backgroundAccent'
  },
  buttonTertiaryText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  calendarBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  calendarDayBackgroundPseudoSelectedHighlighted: {
    kind: 'semantic',
    sourceKey: 'backgroundTertiary'
  },
  calendarDayBackgroundSelected: {
    kind: 'semantic',
    sourceKey: 'backgroundInversePrimary'
  },
  calendarDayBackgroundSelectedHighlighted: {
    kind: 'semantic',
    sourceKey: 'backgroundInversePrimary'
  },
  calendarDayForegroundPseudoSelected: {
    kind: 'semantic',
    sourceKey: 'backgroundInversePrimary'
  },
  calendarDayForegroundPseudoSelectedHighlighted: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  calendarDayForegroundSelected: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  },
  calendarDayForegroundSelectedHighlighted: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  },
  calendarForeground: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  calendarForegroundDisabled: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  calendarHeaderBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  calendarHeaderBackgroundActive: {
    kind: 'semantic',
    sourceKey: 'backgroundInversePrimary'
  },
  calendarHeaderForeground: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  calendarHeaderForegroundDisabled: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  comboboxListItemFocus: {
    kind: 'semantic',
    sourceKey: 'backgroundSecondary'
  },
  comboboxListItemHover: {
    kind: 'semantic',
    sourceKey: 'backgroundTertiary'
  },
  fileUploaderBackgroundColor: {
    kind: 'semantic',
    sourceKey: 'backgroundSecondary'
  },
  fileUploaderBackgroundColorActive: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  fileUploaderBorderColorActive: {
    kind: 'semantic',
    sourceKey: 'borderSelected'
  },
  fileUploaderBorderColorDefault: {
    kind: 'semantic',
    sourceKey: 'borderOpaque'
  },
  fileUploaderMessageColor: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  inputBorder: {
    kind: 'semantic',
    sourceKey: 'borderOpaque'
  },
  inputBorderError: {
    kind: 'semantic',
    sourceKey: 'borderNegative'
  },
  inputBorderPositive: {
    kind: 'semantic',
    sourceKey: 'borderPositive'
  },
  inputEnhancerFill: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  inputEnhancerFillDisabled: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  inputEnhancerTextDisabled: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  inputFill: {
    kind: 'semantic',
    sourceKey: 'backgroundSecondary'
  },
  inputFillActive: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  inputFillDisabled: {
    kind: 'semantic',
    sourceKey: 'backgroundStateDisabled'
  },
  inputFillError: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  inputFillPositive: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  inputPlaceholder: {
    kind: 'semantic',
    sourceKey: 'contentTertiary'
  },
  inputPlaceholderDisabled: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  inputTextDisabled: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  linkActive: {
    kind: 'primitive',
    sourceKey: 'gray600Dark'
  },
  linkHover: {
    kind: 'primitive',
    sourceKey: 'gray700Dark'
  },
  linkText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  linkVisited: {
    kind: 'primitive',
    sourceKey: 'gray500Dark'
  },
  listBodyFill: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  listHeaderFill: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  menuFill: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  menuFillHover: {
    kind: 'semantic',
    sourceKey: 'backgroundSecondary'
  },
  menuFontDefault: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  menuFontDisabled: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  menuFontHighlighted: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  menuFontSelected: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  modalCloseColor: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  modalCloseColorFocus: {
    kind: 'primitive',
    sourceKey: 'gray600Dark'
  },
  modalCloseColorHover: {
    kind: 'primitive',
    sourceKey: 'gray700Dark'
  },
  notificationInfoBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundAccentLight'
  },
  notificationInfoText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  notificationNegativeBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundNegativeLight'
  },
  notificationNegativeText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  notificationPositiveBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPositiveLight'
  },
  notificationPositiveText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  notificationWarningBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundWarningLight'
  },
  notificationWarningText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  progressStepsActiveFill: {
    kind: 'semantic',
    sourceKey: 'backgroundInversePrimary'
  },
  progressStepsActiveText: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  },
  progressStepsCompletedFill: {
    kind: 'semantic',
    sourceKey: 'backgroundInversePrimary'
  },
  progressStepsCompletedText: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  },
  progressbarTrackFill: {
    kind: 'semantic',
    sourceKey: 'backgroundTertiary'
  },
  ratingInactiveFill: {
    kind: 'primitive',
    sourceKey: 'gray500Dark'
  },
  ratingStroke: {
    kind: 'primitive',
    sourceKey: 'gray700Dark'
  },
  sliderHandleFill: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  sliderHandleFillDisabled: {
    kind: 'semantic',
    sourceKey: 'backgroundStateDisabled'
  },
  sliderHandleInnerFill: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  sliderHandleInnerFillDisabled: {
    kind: 'semantic',
    sourceKey: 'backgroundStateDisabled'
  },
  sliderHandleInnerFillSelectedActive: {
    kind: 'primitive',
    sourceKey: 'gray700Dark'
  },
  sliderHandleInnerFillSelectedHover: {
    kind: 'primitive',
    sourceKey: 'gray600Dark'
  },
  sliderTrackFillActive: {
    kind: 'primitive',
    sourceKey: 'gray400Dark'
  },
  sliderTrackFillDisabled: {
    kind: 'semantic',
    sourceKey: 'backgroundStateDisabled'
  },
  sliderTrackFillHover: {
    kind: 'primitive',
    sourceKey: 'gray300Dark'
  },
  spinnerTrackFill: {
    kind: 'semantic',
    sourceKey: 'backgroundTertiary'
  },
  tabBarFill: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  tabColor: {
    kind: 'semantic',
    sourceKey: 'contentTertiary'
  },
  tableBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  tableFilter: {
    kind: 'semantic',
    sourceKey: 'contentTertiary'
  },
  tableFilterBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  tableFilterFooterBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundSecondary'
  },
  tableFilterHeading: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  tableHeadBackgroundColor: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  tableStripedBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundSecondary'
  },
  tagAccentFontDisabled: {
    kind: 'primitive',
    sourceKey: 'blue400Dark'
  },
  tagAccentLightActive: {
    kind: 'primitive',
    sourceKey: 'blue300Dark'
  },
  tagAccentLightBackground: {
    kind: 'primitive',
    sourceKey: 'blue100Dark'
  },
  tagAccentLightDisabled: {
    kind: 'primitive',
    sourceKey: 'blue400Dark'
  },
  tagAccentLightFont: {
    kind: 'primitive',
    sourceKey: 'blue900Dark'
  },
  tagAccentLightFontHover: {
    kind: 'primitive',
    sourceKey: 'blue800Dark'
  },
  tagAccentLightHover: {
    kind: 'primitive',
    sourceKey: 'blue200Dark'
  },
  tagAccentOutlinedActive: {
    kind: 'primitive',
    sourceKey: 'blue700Dark'
  },
  tagAccentOutlinedBackground: {
    kind: 'primitive',
    sourceKey: 'blue100Dark'
  },
  tagAccentOutlinedDisabled: {
    kind: 'primitive',
    sourceKey: 'blue400Dark'
  },
  tagAccentOutlinedFont: {
    kind: 'primitive',
    sourceKey: 'blue700Dark'
  },
  tagAccentOutlinedFontHover: {
    kind: 'primitive',
    sourceKey: 'blue700Dark'
  },
  tagAccentSolidActive: {
    kind: 'primitive',
    sourceKey: 'blue200Dark'
  },
  tagAccentSolidBackground: {
    kind: 'primitive',
    sourceKey: 'blue500Dark'
  },
  tagAccentSolidDisabled: {
    kind: 'primitive',
    sourceKey: 'blue100Dark'
  },
  tagAccentSolidFont: {
    kind: 'primitive',
    sourceKey: 'blue900Dark'
  },
  tagAccentSolidFontHover: {
    kind: 'primitive',
    sourceKey: 'gray800Dark'
  },
  tagAccentSolidHover: {
    kind: 'primitive',
    sourceKey: 'blue300Dark'
  },
  tagNegativeFontDisabled: {
    kind: 'primitive',
    sourceKey: 'red400Dark'
  },
  tagNegativeLightActive: {
    kind: 'primitive',
    sourceKey: 'red300Dark'
  },
  tagNegativeLightBackground: {
    kind: 'primitive',
    sourceKey: 'red100Dark'
  },
  tagNegativeLightDisabled: {
    kind: 'primitive',
    sourceKey: 'red400Dark'
  },
  tagNegativeLightFont: {
    kind: 'primitive',
    sourceKey: 'red900Dark'
  },
  tagNegativeLightFontHover: {
    kind: 'primitive',
    sourceKey: 'red800Dark'
  },
  tagNegativeLightHover: {
    kind: 'primitive',
    sourceKey: 'red200Dark'
  },
  tagNegativeOutlinedActive: {
    kind: 'primitive',
    sourceKey: 'red700Dark'
  },
  tagNegativeOutlinedBackground: {
    kind: 'primitive',
    sourceKey: 'red100Dark'
  },
  tagNegativeOutlinedDisabled: {
    kind: 'primitive',
    sourceKey: 'red400Dark'
  },
  tagNegativeOutlinedFont: {
    kind: 'primitive',
    sourceKey: 'red700Dark'
  },
  tagNegativeOutlinedFontHover: {
    kind: 'primitive',
    sourceKey: 'red700Dark'
  },
  tagNegativeSolidActive: {
    kind: 'primitive',
    sourceKey: 'red200Dark'
  },
  tagNegativeSolidBackground: {
    kind: 'primitive',
    sourceKey: 'red500Dark'
  },
  tagNegativeSolidDisabled: {
    kind: 'primitive',
    sourceKey: 'red100Dark'
  },
  tagNegativeSolidFont: {
    kind: 'primitive',
    sourceKey: 'gray900Dark'
  },
  tagNegativeSolidFontHover: {
    kind: 'primitive',
    sourceKey: 'gray800Dark'
  },
  tagNegativeSolidHover: {
    kind: 'primitive',
    sourceKey: 'red300Dark'
  },
  tagNeutralFontDisabled: {
    kind: 'primitive',
    sourceKey: 'gray400Dark'
  },
  tagNeutralLightActive: {
    kind: 'primitive',
    sourceKey: 'gray700Dark'
  },
  tagNeutralLightBackground: {
    kind: 'primitive',
    sourceKey: 'gray100Dark'
  },
  tagNeutralLightDisabled: {
    kind: 'primitive',
    sourceKey: 'gray400Dark'
  },
  tagNeutralLightFont: {
    kind: 'primitive',
    sourceKey: 'gray900Dark'
  },
  tagNeutralLightFontHover: {
    kind: 'primitive',
    sourceKey: 'gray800Dark'
  },
  tagNeutralLightHover: {
    kind: 'primitive',
    sourceKey: 'gray800Dark'
  },
  tagNeutralOutlinedActive: {
    kind: 'primitive',
    sourceKey: 'gray700Dark'
  },
  tagNeutralOutlinedBackground: {
    kind: 'primitive',
    sourceKey: 'gray100Dark'
  },
  tagNeutralOutlinedDisabled: {
    kind: 'primitive',
    sourceKey: 'gray400Dark'
  },
  tagNeutralOutlinedFont: {
    kind: 'primitive',
    sourceKey: 'gray700Dark'
  },
  tagNeutralOutlinedFontHover: {
    kind: 'primitive',
    sourceKey: 'gray700Dark'
  },
  tagNeutralSolidActive: {
    kind: 'primitive',
    sourceKey: 'gray600Dark'
  },
  tagNeutralSolidBackground: {
    kind: 'primitive',
    sourceKey: 'gray400Dark'
  },
  tagNeutralSolidDisabled: {
    kind: 'primitive',
    sourceKey: 'gray100Dark'
  },
  tagNeutralSolidFont: {
    kind: 'primitive',
    sourceKey: 'gray900Dark'
  },
  tagNeutralSolidFontHover: {
    kind: 'primitive',
    sourceKey: 'gray800Dark'
  },
  tagNeutralSolidHover: {
    kind: 'primitive',
    sourceKey: 'gray700Dark'
  },
  tagPositiveFontDisabled: {
    kind: 'primitive',
    sourceKey: 'green400Dark'
  },
  tagPositiveLightActive: {
    kind: 'primitive',
    sourceKey: 'green300Dark'
  },
  tagPositiveLightBackground: {
    kind: 'primitive',
    sourceKey: 'green100Dark'
  },
  tagPositiveLightDisabled: {
    kind: 'primitive',
    sourceKey: 'green400Dark'
  },
  tagPositiveLightFont: {
    kind: 'primitive',
    sourceKey: 'green900Dark'
  },
  tagPositiveLightFontHover: {
    kind: 'primitive',
    sourceKey: 'green800Dark'
  },
  tagPositiveLightHover: {
    kind: 'primitive',
    sourceKey: 'green200Dark'
  },
  tagPositiveOutlinedActive: {
    kind: 'primitive',
    sourceKey: 'green700Dark'
  },
  tagPositiveOutlinedBackground: {
    kind: 'primitive',
    sourceKey: 'green100Dark'
  },
  tagPositiveOutlinedDisabled: {
    kind: 'primitive',
    sourceKey: 'green400Dark'
  },
  tagPositiveOutlinedFont: {
    kind: 'primitive',
    sourceKey: 'green700Dark'
  },
  tagPositiveOutlinedFontHover: {
    kind: 'primitive',
    sourceKey: 'green700Dark'
  },
  tagPositiveSolidActive: {
    kind: 'primitive',
    sourceKey: 'green200Dark'
  },
  tagPositiveSolidBackground: {
    kind: 'primitive',
    sourceKey: 'green500Dark'
  },
  tagPositiveSolidDisabled: {
    kind: 'primitive',
    sourceKey: 'green100Dark'
  },
  tagPositiveSolidFont: {
    kind: 'primitive',
    sourceKey: 'green900Dark'
  },
  tagPositiveSolidFontHover: {
    kind: 'primitive',
    sourceKey: 'gray800Dark'
  },
  tagPositiveSolidHover: {
    kind: 'primitive',
    sourceKey: 'green300Dark'
  },
  tagPrimaryFontDisabled: {
    kind: 'primitive',
    sourceKey: 'gray400Dark'
  },
  tagPrimaryLightActive: {
    kind: 'primitive',
    sourceKey: 'gray300Dark'
  },
  tagPrimaryLightBackground: {
    kind: 'primitive',
    sourceKey: 'gray100Dark'
  },
  tagPrimaryLightDisabled: {
    kind: 'primitive',
    sourceKey: 'gray400Dark'
  },
  tagPrimaryLightFont: {
    kind: 'primitive',
    sourceKey: 'gray900Dark'
  },
  tagPrimaryLightFontHover: {
    kind: 'primitive',
    sourceKey: 'gray800Dark'
  },
  tagPrimaryLightHover: {
    kind: 'primitive',
    sourceKey: 'gray200Dark'
  },
  tagPrimaryOutlinedActive: {
    kind: 'primitive',
    sourceKey: 'gray700Dark'
  },
  tagPrimaryOutlinedBackground: {
    kind: 'primitive',
    sourceKey: 'gray100Dark'
  },
  tagPrimaryOutlinedDisabled: {
    kind: 'primitive',
    sourceKey: 'gray400Dark'
  },
  tagPrimaryOutlinedFont: {
    kind: 'primitive',
    sourceKey: 'gray700Dark'
  },
  tagPrimaryOutlinedFontHover: {
    kind: 'primitive',
    sourceKey: 'gray700Dark'
  },
  tagPrimarySolidActive: {
    kind: 'primitive',
    sourceKey: 'gray200Dark'
  },
  tagPrimarySolidBackground: {
    kind: 'primitive',
    sourceKey: 'gray400Dark'
  },
  tagPrimarySolidDisabled: {
    kind: 'primitive',
    sourceKey: 'gray100Dark'
  },
  tagPrimarySolidFont: {
    kind: 'primitive',
    sourceKey: 'gray900Dark'
  },
  tagPrimarySolidFontHover: {
    kind: 'primitive',
    sourceKey: 'gray800Dark'
  },
  tagPrimarySolidHover: {
    kind: 'primitive',
    sourceKey: 'gray300Dark'
  },
  tagWarningFontDisabled: {
    kind: 'primitive',
    sourceKey: 'yellow400Dark'
  },
  tagWarningLightActive: {
    kind: 'primitive',
    sourceKey: 'yellow300Dark'
  },
  tagWarningLightBackground: {
    kind: 'primitive',
    sourceKey: 'yellow100Dark'
  },
  tagWarningLightDisabled: {
    kind: 'primitive',
    sourceKey: 'yellow400Dark'
  },
  tagWarningLightFont: {
    kind: 'primitive',
    sourceKey: 'yellow900Dark'
  },
  tagWarningLightFontHover: {
    kind: 'primitive',
    sourceKey: 'yellow800Dark'
  },
  tagWarningLightHover: {
    kind: 'primitive',
    sourceKey: 'yellow200Dark'
  },
  tagWarningOutlinedActive: {
    kind: 'primitive',
    sourceKey: 'yellow700Dark'
  },
  tagWarningOutlinedBackground: {
    kind: 'primitive',
    sourceKey: 'yellow100Dark'
  },
  tagWarningOutlinedDisabled: {
    kind: 'primitive',
    sourceKey: 'yellow400Dark'
  },
  tagWarningOutlinedFont: {
    kind: 'primitive',
    sourceKey: 'yellow700Dark'
  },
  tagWarningOutlinedFontHover: {
    kind: 'primitive',
    sourceKey: 'yellow700Dark'
  },
  tagWarningSolidActive: {
    kind: 'primitive',
    sourceKey: 'yellow200Dark'
  },
  tagWarningSolidBackground: {
    kind: 'primitive',
    sourceKey: 'yellow700Dark'
  },
  tagWarningSolidDisabled: {
    kind: 'primitive',
    sourceKey: 'yellow100Dark'
  },
  tagWarningSolidFont: {
    kind: 'primitive',
    sourceKey: 'yellow50Dark'
  },
  tagWarningSolidFontHover: {
    kind: 'primitive',
    sourceKey: 'gray800Dark'
  },
  tagWarningSolidHover: {
    kind: 'primitive',
    sourceKey: 'yellow300Dark'
  },
  tickBorder: {
    kind: 'semantic',
    sourceKey: 'contentTertiary'
  },
  tickBorderError: {
    kind: 'semantic',
    sourceKey: 'borderNegative'
  },
  tickFill: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  tickFillActive: {
    kind: 'primitive',
    sourceKey: 'gray200Dark'
  },
  tickFillDisabled: {
    kind: 'semantic',
    sourceKey: 'backgroundStateDisabled'
  },
  tickFillError: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  tickFillErrorHover: {
    kind: 'primitive',
    sourceKey: 'gray100Dark'
  },
  tickFillErrorHoverActive: {
    kind: 'primitive',
    sourceKey: 'gray200Dark'
  },
  tickFillErrorSelected: {
    kind: 'semantic',
    sourceKey: 'contentNegative'
  },
  tickFillErrorSelectedHover: {
    kind: 'primitive',
    sourceKey: 'red500Dark'
  },
  tickFillErrorSelectedHoverActive: {
    kind: 'primitive',
    sourceKey: 'red400Dark'
  },
  tickFillHover: {
    kind: 'primitive',
    sourceKey: 'gray100Dark'
  },
  tickFillSelected: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  tickFillSelectedHover: {
    kind: 'primitive',
    sourceKey: 'gray800Dark'
  },
  tickFillSelectedHoverActive: {
    kind: 'primitive',
    sourceKey: 'gray700Dark'
  },
  tickMarkFill: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  },
  tickMarkFillDisabled: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  },
  tickMarkFillError: {
    kind: 'semantic',
    sourceKey: 'contentOnColor'
  },
  toastInfoBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundAccent'
  },
  toastInfoText: {
    kind: 'semantic',
    sourceKey: 'contentOnColor'
  },
  toastNegativeBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundNegative'
  },
  toastNegativeText: {
    kind: 'semantic',
    sourceKey: 'contentOnColor'
  },
  toastPositiveBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPositive'
  },
  toastPositiveText: {
    kind: 'semantic',
    sourceKey: 'contentOnColor'
  },
  toastPrimaryText: {
    kind: 'semantic',
    sourceKey: 'contentOnColor'
  },
  toastText: {
    kind: 'semantic',
    sourceKey: 'contentOnColor'
  },
  toastWarningBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundWarning'
  },
  toastWarningText: {
    kind: 'semantic',
    sourceKey: 'contentOnColorInverse'
  },
  toggleFill: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  toggleFillChecked: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  toggleFillDisabled: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  toggleTrackFill: {
    kind: 'semantic',
    sourceKey: 'backgroundTertiary'
  },
  toggleTrackFillDisabled: {
    kind: 'semantic',
    sourceKey: 'backgroundStateDisabled'
  },
  tooltipBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundInverseSecondary'
  },
  tooltipText: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  }
};
export const LIGHT_COMPONENT_COLOR_PROVENANCE: Record<string, ComponentColorProvenance> = {
  bannerActionHighInfo: {
    kind: 'primitive',
    sourceKey: 'blue700'
  },
  bannerActionHighNegative: {
    kind: 'primitive',
    sourceKey: 'red700'
  },
  bannerActionHighPositive: {
    kind: 'primitive',
    sourceKey: 'green700'
  },
  bannerActionHighWarning: {
    kind: 'primitive',
    sourceKey: 'yellow200'
  },
  bannerActionLowInfo: {
    kind: 'primitive',
    sourceKey: 'blue100'
  },
  bannerActionLowNegative: {
    kind: 'primitive',
    sourceKey: 'red100'
  },
  bannerActionLowPositive: {
    kind: 'primitive',
    sourceKey: 'green100'
  },
  bannerActionLowWarning: {
    kind: 'primitive',
    sourceKey: 'yellow100'
  },
  bottomNavigationSelectedText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  bottomNavigationText: {
    kind: 'primitive',
    sourceKey: 'gray600'
  },
  breadcrumbsSeparatorFill: {
    kind: 'semantic',
    sourceKey: 'contentTertiary'
  },
  breadcrumbsText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  buttonDangerPrimaryActiveOverlay: {
    kind: 'semantic',
    sourceKey: 'pressedOverlayAlpha'
  },
  buttonDangerPrimaryFill: {
    kind: 'semantic',
    sourceKey: 'backgroundNegative'
  },
  buttonDangerPrimaryHoverOverlay: {
    kind: 'semantic',
    sourceKey: 'hoverOverlayAlpha'
  },
  buttonDangerPrimaryLoadingFill: {
    kind: 'semantic',
    sourceKey: 'backgroundNegative'
  },
  buttonDangerPrimarySelectedFill: {
    kind: 'semantic',
    sourceKey: 'backgroundNegative'
  },
  buttonDangerPrimarySelectedText: {
    kind: 'semantic',
    sourceKey: 'contentOnColor'
  },
  buttonDangerPrimarySpinnerBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  buttonDangerPrimarySpinnerForeground: {
    kind: 'semantic',
    sourceKey: 'backgroundAccent'
  },
  buttonDangerPrimaryText: {
    kind: 'semantic',
    sourceKey: 'contentOnColor'
  },
  buttonDangerSecondaryActiveOverlay: {
    kind: 'semantic',
    sourceKey: 'pressedOverlayAlpha'
  },
  buttonDangerSecondaryFill: {
    kind: 'semantic',
    sourceKey: 'backgroundSecondary'
  },
  buttonDangerSecondaryHoverOverlay: {
    kind: 'semantic',
    sourceKey: 'hoverOverlayAlpha'
  },
  buttonDangerSecondaryLoadingFill: {
    kind: 'semantic',
    sourceKey: 'backgroundSecondary'
  },
  buttonDangerSecondarySelectedFill: {
    kind: 'semantic',
    sourceKey: 'backgroundNegative'
  },
  buttonDangerSecondarySelectedText: {
    kind: 'semantic',
    sourceKey: 'contentOnColor'
  },
  buttonDangerSecondarySpinnerBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  buttonDangerSecondarySpinnerForeground: {
    kind: 'semantic',
    sourceKey: 'backgroundAccent'
  },
  buttonDangerSecondaryText: {
    kind: 'semantic',
    sourceKey: 'tagRedContentSecondary'
  },
  buttonDangerTertiaryActiveOverlay: {
    kind: 'semantic',
    sourceKey: 'pressedOverlayAlpha'
  },
  buttonDangerTertiaryHoverOverlay: {
    kind: 'semantic',
    sourceKey: 'hoverOverlayAlpha'
  },
  buttonDangerTertiaryOuterBorder: {
    kind: 'semantic',
    sourceKey: 'tagRedBorderSecondarySelected'
  },
  buttonDangerTertiarySelectedFill: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  buttonDangerTertiarySelectedText: {
    kind: 'semantic',
    sourceKey: 'tagRedContentSecondary'
  },
  buttonDangerTertiarySpinnerBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  buttonDangerTertiarySpinnerForeground: {
    kind: 'semantic',
    sourceKey: 'backgroundAccent'
  },
  buttonDangerTertiaryText: {
    kind: 'semantic',
    sourceKey: 'tagRedContentSecondary'
  },
  buttonDisabledActiveFill: {
    kind: 'semantic',
    sourceKey: 'backgroundStateDisabled'
  },
  buttonDisabledActiveText: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  buttonDisabledFill: {
    kind: 'semantic',
    sourceKey: 'backgroundStateDisabled'
  },
  buttonDisabledSpinnerBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  buttonDisabledSpinnerForeground: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  buttonDisabledText: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  buttonFocusOuterBorder: {
    kind: 'semantic',
    sourceKey: 'borderAccent'
  },
  buttonInnerBorder: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  },
  buttonOuterBorder: {
    kind: 'semantic',
    sourceKey: 'borderSelected'
  },
  buttonOutlineActiveOverlay: {
    kind: 'semantic',
    sourceKey: 'pressedOverlayAlpha'
  },
  buttonOutlineFocusFill: {
    kind: 'semantic',
    sourceKey: 'backgroundTertiary'
  },
  buttonOutlineHoverOverlay: {
    kind: 'semantic',
    sourceKey: 'hoverOverlayAlpha'
  },
  buttonOutlineOuterBorder: {
    kind: 'semantic',
    sourceKey: 'borderOpaque'
  },
  buttonOutlineSelectedText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  buttonOutlineSpinnerBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  buttonOutlineSpinnerForeground: {
    kind: 'semantic',
    sourceKey: 'backgroundAccent'
  },
  buttonOutlineText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  buttonPrimaryActive: {
    kind: 'primitive',
    sourceKey: 'gray800'
  },
  buttonPrimaryActiveOverlay: {
    kind: 'semantic',
    sourceKey: 'pressedOverlayInverseAlpha'
  },
  buttonPrimaryFill: {
    kind: 'semantic',
    sourceKey: 'backgroundInversePrimary'
  },
  buttonPrimaryHover: {
    kind: 'primitive',
    sourceKey: 'gray900'
  },
  buttonPrimaryHoverOverlay: {
    kind: 'semantic',
    sourceKey: 'hoverOverlayInverseAlpha'
  },
  buttonPrimaryLoadingFill: {
    kind: 'semantic',
    sourceKey: 'backgroundInversePrimary'
  },
  buttonPrimarySelectedFill: {
    kind: 'semantic',
    sourceKey: 'backgroundInversePrimary'
  },
  buttonPrimarySelectedText: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  },
  buttonPrimarySpinnerBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  buttonPrimarySpinnerForeground: {
    kind: 'semantic',
    sourceKey: 'backgroundAccent'
  },
  buttonPrimaryText: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  },
  buttonSecondaryActive: {
    kind: 'primitive',
    sourceKey: 'gray300'
  },
  buttonSecondaryActiveOverlay: {
    kind: 'semantic',
    sourceKey: 'pressedOverlayAlpha'
  },
  buttonSecondaryFill: {
    kind: 'semantic',
    sourceKey: 'backgroundSecondary'
  },
  buttonSecondaryHover: {
    kind: 'primitive',
    sourceKey: 'gray200'
  },
  buttonSecondaryHoverOverlay: {
    kind: 'semantic',
    sourceKey: 'hoverOverlayAlpha'
  },
  buttonSecondaryLoadingFill: {
    kind: 'semantic',
    sourceKey: 'backgroundSecondary'
  },
  buttonSecondarySelectedFill: {
    kind: 'semantic',
    sourceKey: 'backgroundInversePrimary'
  },
  buttonSecondarySelectedText: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  },
  buttonSecondarySpinnerBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  buttonSecondarySpinnerForeground: {
    kind: 'semantic',
    sourceKey: 'backgroundAccent'
  },
  buttonSecondaryText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  buttonTertiaryActive: {
    kind: 'primitive',
    sourceKey: 'gray100'
  },
  buttonTertiaryActiveOverlay: {
    kind: 'semantic',
    sourceKey: 'pressedOverlayAlpha'
  },
  buttonTertiaryDisabledActiveFill: {
    kind: 'semantic',
    sourceKey: 'backgroundStateDisabled'
  },
  buttonTertiaryDisabledActiveText: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  buttonTertiaryFocusFill: {
    kind: 'semantic',
    sourceKey: 'backgroundTertiary'
  },
  buttonTertiaryHover: {
    kind: 'primitive',
    sourceKey: 'gray50'
  },
  buttonTertiaryHoverOverlay: {
    kind: 'semantic',
    sourceKey: 'hoverOverlayAlpha'
  },
  buttonTertiarySelectedText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  buttonTertiarySpinnerBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundTertiary'
  },
  buttonTertiarySpinnerForeground: {
    kind: 'semantic',
    sourceKey: 'backgroundAccent'
  },
  buttonTertiaryText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  calendarBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  calendarDayBackgroundPseudoSelectedHighlighted: {
    kind: 'semantic',
    sourceKey: 'backgroundTertiary'
  },
  calendarDayBackgroundSelected: {
    kind: 'semantic',
    sourceKey: 'backgroundInversePrimary'
  },
  calendarDayBackgroundSelectedHighlighted: {
    kind: 'semantic',
    sourceKey: 'backgroundInversePrimary'
  },
  calendarDayForegroundPseudoSelected: {
    kind: 'semantic',
    sourceKey: 'backgroundInversePrimary'
  },
  calendarDayForegroundPseudoSelectedHighlighted: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  calendarDayForegroundSelected: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  },
  calendarDayForegroundSelectedHighlighted: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  },
  calendarForeground: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  calendarForegroundDisabled: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  calendarHeaderBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  calendarHeaderBackgroundActive: {
    kind: 'semantic',
    sourceKey: 'backgroundInversePrimary'
  },
  calendarHeaderForeground: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  calendarHeaderForegroundDisabled: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  comboboxListItemFocus: {
    kind: 'semantic',
    sourceKey: 'backgroundSecondary'
  },
  comboboxListItemHover: {
    kind: 'semantic',
    sourceKey: 'backgroundTertiary'
  },
  fileUploaderBackgroundColor: {
    kind: 'semantic',
    sourceKey: 'backgroundSecondary'
  },
  fileUploaderBackgroundColorActive: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  fileUploaderBorderColorActive: {
    kind: 'semantic',
    sourceKey: 'borderSelected'
  },
  fileUploaderBorderColorDefault: {
    kind: 'semantic',
    sourceKey: 'borderOpaque'
  },
  fileUploaderMessageColor: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  inputBorder: {
    kind: 'semantic',
    sourceKey: 'borderOpaque'
  },
  inputBorderError: {
    kind: 'semantic',
    sourceKey: 'borderNegative'
  },
  inputBorderPositive: {
    kind: 'semantic',
    sourceKey: 'borderPositive'
  },
  inputEnhancerFill: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  inputEnhancerFillDisabled: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  inputEnhancerTextDisabled: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  inputFill: {
    kind: 'semantic',
    sourceKey: 'backgroundSecondary'
  },
  inputFillActive: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  inputFillDisabled: {
    kind: 'semantic',
    sourceKey: 'backgroundStateDisabled'
  },
  inputFillError: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  inputFillPositive: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  inputPlaceholder: {
    kind: 'semantic',
    sourceKey: 'contentTertiary'
  },
  inputPlaceholderDisabled: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  inputTextDisabled: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  linkActive: {
    kind: 'primitive',
    sourceKey: 'gray700'
  },
  linkHover: {
    kind: 'primitive',
    sourceKey: 'gray800'
  },
  linkText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  linkVisited: {
    kind: 'primitive',
    sourceKey: 'gray600'
  },
  listBodyFill: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  listHeaderFill: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  menuFill: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  menuFillHover: {
    kind: 'semantic',
    sourceKey: 'backgroundSecondary'
  },
  menuFontDefault: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  menuFontDisabled: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  menuFontHighlighted: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  menuFontSelected: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  modalCloseColor: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  modalCloseColorFocus: {
    kind: 'primitive',
    sourceKey: 'gray800'
  },
  modalCloseColorHover: {
    kind: 'primitive',
    sourceKey: 'gray900'
  },
  notificationInfoBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundAccentLight'
  },
  notificationInfoText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  notificationNegativeBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundNegativeLight'
  },
  notificationNegativeText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  notificationPositiveBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPositiveLight'
  },
  notificationPositiveText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  notificationWarningBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundWarningLight'
  },
  notificationWarningText: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  progressStepsActiveFill: {
    kind: 'semantic',
    sourceKey: 'backgroundInversePrimary'
  },
  progressStepsActiveText: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  },
  progressStepsCompletedFill: {
    kind: 'semantic',
    sourceKey: 'backgroundInversePrimary'
  },
  progressStepsCompletedText: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  },
  progressbarTrackFill: {
    kind: 'semantic',
    sourceKey: 'backgroundTertiary'
  },
  ratingInactiveFill: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  ratingStroke: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  sliderHandleFill: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  sliderHandleFillDisabled: {
    kind: 'semantic',
    sourceKey: 'backgroundStateDisabled'
  },
  sliderHandleInnerFill: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  sliderHandleInnerFillDisabled: {
    kind: 'semantic',
    sourceKey: 'backgroundStateDisabled'
  },
  sliderHandleInnerFillSelectedActive: {
    kind: 'primitive',
    sourceKey: 'gray800'
  },
  sliderHandleInnerFillSelectedHover: {
    kind: 'primitive',
    sourceKey: 'gray900'
  },
  sliderTrackFillActive: {
    kind: 'primitive',
    sourceKey: 'gray300'
  },
  sliderTrackFillDisabled: {
    kind: 'semantic',
    sourceKey: 'backgroundStateDisabled'
  },
  sliderTrackFillHover: {
    kind: 'primitive',
    sourceKey: 'gray200'
  },
  spinnerTrackFill: {
    kind: 'semantic',
    sourceKey: 'backgroundTertiary'
  },
  tabBarFill: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  tabColor: {
    kind: 'semantic',
    sourceKey: 'contentTertiary'
  },
  tableBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  tableFilter: {
    kind: 'semantic',
    sourceKey: 'contentTertiary'
  },
  tableFilterBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  tableFilterFooterBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundSecondary'
  },
  tableFilterHeading: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  tableHeadBackgroundColor: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  tableStripedBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundSecondary'
  },
  tagAccentFontDisabled: {
    kind: 'primitive',
    sourceKey: 'blue300'
  },
  tagAccentLightActive: {
    kind: 'primitive',
    sourceKey: 'blue200'
  },
  tagAccentLightBackground: {
    kind: 'primitive',
    sourceKey: 'blue50'
  },
  tagAccentLightDisabled: {
    kind: 'primitive',
    sourceKey: 'blue50'
  },
  tagAccentLightFont: {
    kind: 'primitive',
    sourceKey: 'blue600'
  },
  tagAccentLightFontHover: {
    kind: 'primitive',
    sourceKey: 'blue700'
  },
  tagAccentLightHover: {
    kind: 'primitive',
    sourceKey: 'blue100'
  },
  tagAccentOutlinedActive: {
    kind: 'primitive',
    sourceKey: 'blue800'
  },
  tagAccentOutlinedBackground: {
    kind: 'primitive',
    sourceKey: 'blue50'
  },
  tagAccentOutlinedDisabled: {
    kind: 'primitive',
    sourceKey: 'blue200'
  },
  tagAccentOutlinedFont: {
    kind: 'primitive',
    sourceKey: 'blue700'
  },
  tagAccentOutlinedFontHover: {
    kind: 'primitive',
    sourceKey: 'blue700'
  },
  tagAccentSolidActive: {
    kind: 'primitive',
    sourceKey: 'blue100'
  },
  tagAccentSolidBackground: {
    kind: 'primitive',
    sourceKey: 'blue600'
  },
  tagAccentSolidDisabled: {
    kind: 'primitive',
    sourceKey: 'blue50'
  },
  tagAccentSolidFont: {
    kind: 'primitive',
    sourceKey: 'white'
  },
  tagAccentSolidFontHover: {
    kind: 'primitive',
    sourceKey: 'blue500'
  },
  tagAccentSolidHover: {
    kind: 'primitive',
    sourceKey: 'blue50'
  },
  tagNegativeFontDisabled: {
    kind: 'primitive',
    sourceKey: 'red300'
  },
  tagNegativeLightActive: {
    kind: 'primitive',
    sourceKey: 'red200'
  },
  tagNegativeLightBackground: {
    kind: 'primitive',
    sourceKey: 'red50'
  },
  tagNegativeLightDisabled: {
    kind: 'primitive',
    sourceKey: 'red50'
  },
  tagNegativeLightFont: {
    kind: 'primitive',
    sourceKey: 'red600'
  },
  tagNegativeLightFontHover: {
    kind: 'primitive',
    sourceKey: 'red700'
  },
  tagNegativeLightHover: {
    kind: 'primitive',
    sourceKey: 'red100'
  },
  tagNegativeOutlinedActive: {
    kind: 'primitive',
    sourceKey: 'red800'
  },
  tagNegativeOutlinedBackground: {
    kind: 'primitive',
    sourceKey: 'red50'
  },
  tagNegativeOutlinedDisabled: {
    kind: 'primitive',
    sourceKey: 'red200'
  },
  tagNegativeOutlinedFont: {
    kind: 'primitive',
    sourceKey: 'red700'
  },
  tagNegativeOutlinedFontHover: {
    kind: 'primitive',
    sourceKey: 'red700'
  },
  tagNegativeSolidActive: {
    kind: 'primitive',
    sourceKey: 'red100'
  },
  tagNegativeSolidBackground: {
    kind: 'primitive',
    sourceKey: 'red600'
  },
  tagNegativeSolidDisabled: {
    kind: 'primitive',
    sourceKey: 'red50'
  },
  tagNegativeSolidFont: {
    kind: 'primitive',
    sourceKey: 'white'
  },
  tagNegativeSolidFontHover: {
    kind: 'primitive',
    sourceKey: 'red500'
  },
  tagNegativeSolidHover: {
    kind: 'primitive',
    sourceKey: 'red50'
  },
  tagNeutralFontDisabled: {
    kind: 'primitive',
    sourceKey: 'gray200'
  },
  tagNeutralLightActive: {
    kind: 'primitive',
    sourceKey: 'gray100'
  },
  tagNeutralLightBackground: {
    kind: 'primitive',
    sourceKey: 'white'
  },
  tagNeutralLightDisabled: {
    kind: 'primitive',
    sourceKey: 'gray200'
  },
  tagNeutralLightFont: {
    kind: 'primitive',
    sourceKey: 'gray600'
  },
  tagNeutralLightFontHover: {
    kind: 'primitive',
    sourceKey: 'gray700'
  },
  tagNeutralLightHover: {
    kind: 'primitive',
    sourceKey: 'gray50'
  },
  tagNeutralOutlinedActive: {
    kind: 'primitive',
    sourceKey: 'gray800'
  },
  tagNeutralOutlinedBackground: {
    kind: 'primitive',
    sourceKey: 'gray50'
  },
  tagNeutralOutlinedDisabled: {
    kind: 'primitive',
    sourceKey: 'gray200'
  },
  tagNeutralOutlinedFont: {
    kind: 'primitive',
    sourceKey: 'gray700'
  },
  tagNeutralOutlinedFontHover: {
    kind: 'primitive',
    sourceKey: 'gray700'
  },
  tagNeutralSolidActive: {
    kind: 'primitive',
    sourceKey: 'gray800'
  },
  tagNeutralSolidBackground: {
    kind: 'primitive',
    sourceKey: 'gray600'
  },
  tagNeutralSolidDisabled: {
    kind: 'primitive',
    sourceKey: 'gray200'
  },
  tagNeutralSolidFont: {
    kind: 'primitive',
    sourceKey: 'white'
  },
  tagNeutralSolidFontHover: {
    kind: 'primitive',
    sourceKey: 'gray700'
  },
  tagNeutralSolidHover: {
    kind: 'primitive',
    sourceKey: 'gray900'
  },
  tagPositiveFontDisabled: {
    kind: 'primitive',
    sourceKey: 'green300'
  },
  tagPositiveLightActive: {
    kind: 'primitive',
    sourceKey: 'green200'
  },
  tagPositiveLightBackground: {
    kind: 'primitive',
    sourceKey: 'green50'
  },
  tagPositiveLightDisabled: {
    kind: 'primitive',
    sourceKey: 'green50'
  },
  tagPositiveLightFont: {
    kind: 'primitive',
    sourceKey: 'green600'
  },
  tagPositiveLightFontHover: {
    kind: 'primitive',
    sourceKey: 'green700'
  },
  tagPositiveLightHover: {
    kind: 'primitive',
    sourceKey: 'green100'
  },
  tagPositiveOutlinedActive: {
    kind: 'primitive',
    sourceKey: 'green800'
  },
  tagPositiveOutlinedBackground: {
    kind: 'primitive',
    sourceKey: 'green50'
  },
  tagPositiveOutlinedDisabled: {
    kind: 'primitive',
    sourceKey: 'green200'
  },
  tagPositiveOutlinedFont: {
    kind: 'primitive',
    sourceKey: 'green700'
  },
  tagPositiveOutlinedFontHover: {
    kind: 'primitive',
    sourceKey: 'green700'
  },
  tagPositiveSolidActive: {
    kind: 'primitive',
    sourceKey: 'green100'
  },
  tagPositiveSolidBackground: {
    kind: 'primitive',
    sourceKey: 'green600'
  },
  tagPositiveSolidDisabled: {
    kind: 'primitive',
    sourceKey: 'green50'
  },
  tagPositiveSolidFont: {
    kind: 'primitive',
    sourceKey: 'white'
  },
  tagPositiveSolidFontHover: {
    kind: 'primitive',
    sourceKey: 'green500'
  },
  tagPositiveSolidHover: {
    kind: 'primitive',
    sourceKey: 'green50'
  },
  tagPrimaryFontDisabled: {
    kind: 'primitive',
    sourceKey: 'gray300'
  },
  tagPrimaryLightActive: {
    kind: 'primitive',
    sourceKey: 'gray100'
  },
  tagPrimaryLightBackground: {
    kind: 'primitive',
    sourceKey: 'white'
  },
  tagPrimaryLightDisabled: {
    kind: 'primitive',
    sourceKey: 'gray200'
  },
  tagPrimaryLightFont: {
    kind: 'primitive',
    sourceKey: 'black'
  },
  tagPrimaryLightFontHover: {
    kind: 'primitive',
    sourceKey: 'gray900'
  },
  tagPrimaryLightHover: {
    kind: 'primitive',
    sourceKey: 'gray50'
  },
  tagPrimaryOutlinedActive: {
    kind: 'primitive',
    sourceKey: 'gray900'
  },
  tagPrimaryOutlinedBackground: {
    kind: 'primitive',
    sourceKey: 'gray50'
  },
  tagPrimaryOutlinedDisabled: {
    kind: 'primitive',
    sourceKey: 'gray200'
  },
  tagPrimaryOutlinedFont: {
    kind: 'primitive',
    sourceKey: 'gray700'
  },
  tagPrimaryOutlinedFontHover: {
    kind: 'primitive',
    sourceKey: 'gray900'
  },
  tagPrimarySolidActive: {
    kind: 'primitive',
    sourceKey: 'gray900'
  },
  tagPrimarySolidBackground: {
    kind: 'primitive',
    sourceKey: 'gray600'
  },
  tagPrimarySolidDisabled: {
    kind: 'primitive',
    sourceKey: 'gray200'
  },
  tagPrimarySolidFont: {
    kind: 'primitive',
    sourceKey: 'white'
  },
  tagPrimarySolidFontHover: {
    kind: 'primitive',
    sourceKey: 'gray900'
  },
  tagPrimarySolidHover: {
    kind: 'primitive',
    sourceKey: 'gray900'
  },
  tagWarningFontDisabled: {
    kind: 'primitive',
    sourceKey: 'yellow200'
  },
  tagWarningLightActive: {
    kind: 'primitive',
    sourceKey: 'yellow200'
  },
  tagWarningLightBackground: {
    kind: 'primitive',
    sourceKey: 'yellow50'
  },
  tagWarningLightDisabled: {
    kind: 'primitive',
    sourceKey: 'yellow50'
  },
  tagWarningLightFont: {
    kind: 'primitive',
    sourceKey: 'yellow600'
  },
  tagWarningLightFontHover: {
    kind: 'primitive',
    sourceKey: 'yellow700'
  },
  tagWarningLightHover: {
    kind: 'primitive',
    sourceKey: 'yellow100'
  },
  tagWarningOutlinedActive: {
    kind: 'primitive',
    sourceKey: 'yellow800'
  },
  tagWarningOutlinedBackground: {
    kind: 'primitive',
    sourceKey: 'yellow50'
  },
  tagWarningOutlinedDisabled: {
    kind: 'primitive',
    sourceKey: 'yellow200'
  },
  tagWarningOutlinedFont: {
    kind: 'primitive',
    sourceKey: 'yellow700'
  },
  tagWarningOutlinedFontHover: {
    kind: 'primitive',
    sourceKey: 'yellow700'
  },
  tagWarningSolidActive: {
    kind: 'primitive',
    sourceKey: 'yellow100'
  },
  tagWarningSolidBackground: {
    kind: 'primitive',
    sourceKey: 'yellow300'
  },
  tagWarningSolidDisabled: {
    kind: 'primitive',
    sourceKey: 'yellow50'
  },
  tagWarningSolidFont: {
    kind: 'primitive',
    sourceKey: 'yellow900'
  },
  tagWarningSolidFontHover: {
    kind: 'primitive',
    sourceKey: 'yellow500'
  },
  tagWarningSolidHover: {
    kind: 'primitive',
    sourceKey: 'yellow50'
  },
  tickBorder: {
    kind: 'semantic',
    sourceKey: 'contentTertiary'
  },
  tickBorderError: {
    kind: 'semantic',
    sourceKey: 'borderNegative'
  },
  tickFill: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  tickFillActive: {
    kind: 'primitive',
    sourceKey: 'gray100'
  },
  tickFillDisabled: {
    kind: 'semantic',
    sourceKey: 'backgroundStateDisabled'
  },
  tickFillError: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  tickFillErrorHover: {
    kind: 'primitive',
    sourceKey: 'gray50'
  },
  tickFillErrorHoverActive: {
    kind: 'primitive',
    sourceKey: 'gray100'
  },
  tickFillErrorSelected: {
    kind: 'semantic',
    sourceKey: 'contentNegative'
  },
  tickFillErrorSelectedHover: {
    kind: 'primitive',
    sourceKey: 'red700'
  },
  tickFillErrorSelectedHoverActive: {
    kind: 'primitive',
    sourceKey: 'red800'
  },
  tickFillHover: {
    kind: 'primitive',
    sourceKey: 'gray50'
  },
  tickFillSelected: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  tickFillSelectedHover: {
    kind: 'primitive',
    sourceKey: 'gray900'
  },
  tickFillSelectedHoverActive: {
    kind: 'primitive',
    sourceKey: 'gray800'
  },
  tickMarkFill: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  },
  tickMarkFillDisabled: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  },
  tickMarkFillError: {
    kind: 'semantic',
    sourceKey: 'contentOnColor'
  },
  toastInfoBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundAccent'
  },
  toastInfoText: {
    kind: 'semantic',
    sourceKey: 'contentOnColor'
  },
  toastNegativeBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundNegative'
  },
  toastNegativeText: {
    kind: 'semantic',
    sourceKey: 'contentOnColor'
  },
  toastPositiveBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundPositive'
  },
  toastPositiveText: {
    kind: 'semantic',
    sourceKey: 'contentOnColor'
  },
  toastPrimaryText: {
    kind: 'semantic',
    sourceKey: 'contentOnColor'
  },
  toastText: {
    kind: 'semantic',
    sourceKey: 'contentOnColor'
  },
  toastWarningBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundWarning'
  },
  toastWarningText: {
    kind: 'semantic',
    sourceKey: 'contentOnColorInverse'
  },
  toggleFill: {
    kind: 'semantic',
    sourceKey: 'backgroundPrimary'
  },
  toggleFillChecked: {
    kind: 'semantic',
    sourceKey: 'contentPrimary'
  },
  toggleFillDisabled: {
    kind: 'semantic',
    sourceKey: 'contentStateDisabled'
  },
  toggleTrackFill: {
    kind: 'semantic',
    sourceKey: 'backgroundTertiary'
  },
  toggleTrackFillDisabled: {
    kind: 'semantic',
    sourceKey: 'backgroundStateDisabled'
  },
  tooltipBackground: {
    kind: 'semantic',
    sourceKey: 'backgroundInverseSecondary'
  },
  tooltipText: {
    kind: 'semantic',
    sourceKey: 'contentInversePrimary'
  }
};